import { Link } from "react-router-dom";
import { ClipboardList, CalendarClock, Video, Send } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, EmptyState, StatusBadge, CandidateAvatar } from "@/components/Common";
import { ContractButton, CountersignButton, FeeStatus } from "@/components/Signature";
import { api, formatErr, useFetch, fmtDate, fmtDay } from "@/lib/api";

const Row = ({ item, children, testId }) => (
  <div data-testid={testId} className="flex flex-col gap-4 rounded-md border border-stone-200 bg-white p-5 sm:flex-row sm:items-center">
    <CandidateAvatar candidate={item.candidate} className="h-12 w-12" />
    <div className="min-w-0 flex-1">
      <Link to={`/app/candidates/${item.candidate.id}`} className="font-display font-bold text-stone-900 hover:underline">{item.candidate.name}</Link>
      <p className="text-sm text-stone-500">{item.candidate.title}</p>
      <div className="mt-1.5 text-xs text-stone-500">{children}</div>
    </div>
    <div className="flex flex-col items-start gap-1 sm:items-end">
      <StatusBadge status={item.status} />
      <span className="text-[11px] font-mono text-stone-400">Sent {fmtDate(item.created_at)}</span>
    </div>
  </div>
);

export default function Requests() {
  const { data: hires } = useFetch("/hire-requests");
  const { data: ints } = useFetch("/interviews");
  const { data: offers, setData: setOffers } = useFetch("/offers");
  const withdraw = async (o) => {
    try {
      const { data } = await api.post(`/offers/${o.id}/withdraw`);
      setOffers((p) => p.map((x) => (x.id === o.id ? data : x)));
      toast.success("Offer withdrawn");
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Pipeline" title="Hires, interviews & offers" desc="Track every request and offer you've sent." />
      <Tabs defaultValue="interviews">
        <TabsList className="mb-6">
          <TabsTrigger value="offers" data-testid="tab-offers">Offers ({offers?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="interviews" data-testid="tab-interviews">Interviews ({ints?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="hires" data-testid="tab-hires">Hire requests ({hires?.length ?? 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="offers" className="space-y-3">
          {offers?.length === 0 && <EmptyState testId="offers-empty" icon={Send} title="No offers sent yet" desc="Open a candidate profile and click Send offer." />}
          {(offers || []).map((o) => (
            <Row key={o.id} item={o} testId={`offer-row-${o.id}`}>
              {o.position} · ${o.hourly_rate}/hr · {o.hours_per_week} hrs/wk{o.licensing_fee > 0 && ` · fee $${o.licensing_fee}`}
              {o.response_note && <p className="mt-1 italic">Candidate: {o.response_note}</p>}
              <ContractButton offer={o} className="mt-1 block" />
              <CountersignButton offer={o} onSigned={(n) => setOffers((p) => p.map((x) => (x.id === n.id ? n : x)))} />
              <FeeStatus offer={o} canPay />
              {o.status === "Pending" && <button data-testid={`withdraw-offer-${o.id}`} onClick={() => withdraw(o)} className="mt-1 block text-red-600 underline">Withdraw</button>}
            </Row>
          ))}
        </TabsContent>
        <TabsContent value="interviews" className="space-y-3">
          {ints?.length === 0 && <EmptyState testId="interviews-empty" icon={CalendarClock} title="No interview requests yet" desc="Open a candidate profile and click Request interview." />}
          {(ints || []).map((i) => (
            <Row key={i.id} item={i} testId={`interview-row-${i.id}`}>
              {i.scheduled_at ? <span className="font-medium text-blue-700">Confirmed: {fmtDate(i.scheduled_at)}</span> : <span>Proposed: {i.preferred_times.map(fmtDate).join(" · ")}</span>}
              {i.meeting_link && <a href={i.meeting_link} target="_blank" rel="noreferrer" data-testid={`interview-link-${i.id}`} className="ml-3 inline-flex items-center gap-1 font-medium text-slate-900 underline"><Video className="h-3 w-3" />Join meeting</a>}
              {i.admin_note && <p className="mt-1 italic">Team note: {i.admin_note}</p>}
            </Row>
          ))}
        </TabsContent>
        <TabsContent value="hires" className="space-y-3">
          {hires?.length === 0 && <EmptyState testId="hires-empty" icon={ClipboardList} title="No hire requests yet" desc="Found the right person? Click Request to hire on their profile." />}
          {(hires || []).map((h) => (
            <Row key={h.id} item={h} testId={`hire-row-${h.id}`}>
              {h.engagement} · {h.hours_per_week} hrs/week{h.start_date && ` · Start ${fmtDay(h.start_date)}`}
              {h.admin_note && <p className="mt-1 italic">Team note: {h.admin_note}</p>}
            </Row>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
